import { mergeAttributes, Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import DaVideoView from './components/DaVideoView';
import { deleteExtension } from '../../components/utils/tiptap';

export interface VideoData {
  src: string;
}

export interface DaVideoOptions {
  defaultWidth: number;
  defaultHeight: number;
  smallMedia?: boolean;
  canAlign?: boolean;
  defaultAlignment?: string;
}

export interface DaVideoAttrs {
  src: string;
  alignment: 'left' | 'right' | 'center';
}

export enum MenuState {
  HIDDEN,
  SHOW,
}

export type DaVideoStorage = {
  menuState: MenuState;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    daVideo: {
      daVideoAlign: (alignment: 'left' | 'right' | 'center') => ReturnType;
      daVideoOpenSettingsMenu: () => ReturnType;
      daVideoCloseSettingsMenu: () => ReturnType;
      daVideoReplace: (video: VideoData) => ReturnType;
      daVideoDelete: () => ReturnType;
    };
  }
}

export const DaVideo = Node.create<DaVideoOptions, DaVideoStorage>({
  name: 'da-video',

  group: 'block unlinkable',
  inline: false,
  needsFixAfterPaste: false,

  addStorage() {
    return {
      menuState: MenuState.HIDDEN,
    };
  },

  addOptions() {
    return {
      HTMLAttributes: {},
      canAlign: true,
      defaultWidth: 780,
      defaultHeight: 438,
      smallMedia: false,
    };
  },

  addAttributes() {
    return {
      src: {
        default: null,
      },
      alignment: {
        default: this.options.defaultAlignment ?? 'center',
      },
    };
  },

  /**
   * Used to parse incoming html, usually via paste
   */
  parseHTML() {
    return [
      {
        tag: `figure[data-da-type="${this.name}"]`,
        getAttrs(node) {
          if (!node || !node.getAttribute) {
            return false;
          }

          const dataType = node.getAttribute('data-da-type');
          if (!dataType || dataType !== this.name) {
            return false;
          }
          try {
            return {
              src: node.getAttribute('data-src') ?? '',
              alignment: node.getAttribute('data-alignment') ?? undefined,
            };
          } catch (e) {}
          return null;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes: { src, alignment } }) {
    return [
      'div',
      mergeAttributes({
        'data-da-type': this.name,
        'data-src': src,
        'data-alignment': alignment,
        class: 'node-da-video',
      }),
      ['div', { class: 'react-video-placeholder' }],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(DaVideoView);
  },

  onSelectionUpdate() {
    if (this.editor.isActive('da-video')) {
      this.editor.commands.daVideoOpenSettingsMenu();
    } else {
      this.editor.commands.daVideoCloseSettingsMenu();
    }
  },

  addCommands() {
    return {
      daVideoOpenSettingsMenu:
        () =>
        ({ editor, chain, dispatch }) => {
          if (dispatch) {
            this.storage.menuState = MenuState.SHOW;
          }
          return true;
        },
      daVideoCloseSettingsMenu:
        () =>
        ({ editor, chain, dispatch }) => {
          if (dispatch) {
            this.storage.menuState = MenuState.HIDDEN;
          }
          return true;
        },
      daVideoAlign:
        alignment =>
        ({ editor, chain, dispatch, commands }) => {
          if (!this.options.canAlign) {
            return false;
          }
          if (dispatch) {
            return commands.updateAttributes(this.name, {
              alignment,
            });
          }
          return true;
        },
      daVideoReplace:
        ({ src }) =>
        ({ editor, chain, dispatch, commands, state }) => {
          this.storage.menuState = MenuState.SHOW;
          if (dispatch) {
            commands.updateAttributes(this.name, {
              src,
            });
            return true;
          }
          return true;
        },
      daVideoDelete:
        () =>
        ({ editor, chain, dispatch, commands, state }) => {
          if (deleteExtension('da-video', editor, dispatch, state)) {
            this.storage.menuState = MenuState.HIDDEN;
            return true;
          }
          return true;
        },
    };
  },
});
